import React, { useState, useEffect } from 'react';
import CountryFlag, { countryFromRegion } from '../../../shared/components/CountryFlag';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Progress,
    message,
    Spin,
    Empty
} from 'antd';
import {
    CloudServerOutlined,
    DatabaseOutlined,
    CloudOutlined,
    GlobalOutlined,
    HddOutlined,
    ThunderboltOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../shared/services/hostSharedService';
import hostSharedAdminService from '../../services/hostSharedAdminService';

/**
 * 流量：shared_quotas.bandwidth_quota / bandwidth_used 单位是 GB（GB/月），不是 MB
 */
const formatGB = (gb) => {
    const value = Number(gb) || 0;
    if (value >= 1024) return `${(value / 1024).toFixed(2)} TB`;
    return `${Number.isInteger(value) ? value : value.toFixed(2)} GB`;
};

/**
 * 将 MB 数值格式化为易读的存储单位（磁盘）
 */
const formatMB = (mb) => {
    const value = Number(mb) || 0;
    if (value >= 1024 * 1024) return `${(value / 1024 / 1024).toFixed(2)} TB`;
    if (value >= 1024) return `${(value / 1024).toFixed(2)} GB`;
    return `${value.toFixed(0)} MB`;
};

const Statistics = () => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        total_nodes: 0,
        online_nodes: 0,
        total_vhosts: 0,
        active_vhosts: 0,
        total_domains: 0,
        total_databases: 0,
        disk_usage: 0,
        bandwidth_usage: 0,
        total_disk_quota: 0,
        total_bandwidth_quota: 0
    });
    const [nodes, setNodes] = useState([]);

    useEffect(() => {
        loadStatistics();
    }, []);

    const loadStatistics = async () => {
        setLoading(true);
        try {
            // 使用专用聚合端点，避免全量拉取 vhost 仅做 count
            const [statsResponse, nodesResponse] = await Promise.all([
                hostSharedAdminService.getStats(),
                hostSharedService.getNodes()
            ]);

            if (statsResponse.success) {
                setStats({ ...stats, ...(statsResponse.data || {}) });
            } else {
                message.error(statsResponse.error || '加载统计信息失败');
            }

            if (nodesResponse.success) {
                const nodesList = nodesResponse.data?.list || nodesResponse.data || [];
                setNodes(Array.isArray(nodesList) ? nodesList : []);
            }
        } catch (error) {
            message.error('加载统计信息失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const nodeColumns = [
        {
            title: '节点名称',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '地区',
            dataIndex: 'region',
            key: 'region',
            render: (text) => (text ? <><CountryFlag code={countryFromRegion(text)} />{text}</> : '-'),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const statusMap = {
                    'online': { icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />, text: '在线' },
                    'offline': { icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />, text: '离线' },
                    'maintenance': { icon: <CloseCircleOutlined style={{ color: '#faad14' }} />, text: '维护中' }
                };
                const config = statusMap[status] || { icon: null, text: '未知' };
                return (
                    <span>
                        {config.icon} {config.text}
                    </span>
                );
            }
        },
        {
            title: '容量',
            key: 'capacity',
            render: (_, record) => `${record.current_count || 0} / ${record.capacity || 0}`,
        },
        {
            title: '使用率',
            key: 'usage',
            render: (_, record) => {
                const percent = record.capacity > 0
                    ? Math.round((record.current_count / record.capacity) * 100)
                    : 0;
                return (
                    <Progress
                        percent={percent}
                        size="small"
                        status={percent >= 90 ? 'exception' : percent >= 70 ? 'normal' : 'success'}
                    />
                );
            },
        },
        {
            title: '优先级',
            dataIndex: 'priority',
            key: 'priority',
        },
        {
            title: '最后检查',
            dataIndex: 'last_check',
            key: 'last_check',
            render: (text) => text || '-',
        },
    ];

    const diskPercent = stats.total_disk_quota > 0
        ? Math.round((stats.disk_usage / stats.total_disk_quota) * 100)
        : 0;
    const bandwidthPercent = stats.total_bandwidth_quota > 0
        ? Math.round((stats.bandwidth_usage / stats.total_bandwidth_quota) * 100)
        : 0;

    if (loading && !stats.total_nodes) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="总节点数"
                            value={stats.total_nodes}
                            prefix={<CloudServerOutlined />}
                            suffix={`/ ${stats.online_nodes} 在线`}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="总虚拟主机"
                            value={stats.total_vhosts}
                            prefix={<CloudOutlined />}
                            suffix={`/ ${stats.active_vhosts} 运行中`}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="绑定域名总数"
                            value={stats.total_domains}
                            prefix={<GlobalOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="数据库总数"
                            value={stats.total_databases}
                            prefix={<DatabaseOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} lg={12}>
                    <Card title={<span><HddOutlined /> 磁盘用量</span>}>
                        <Progress
                            percent={diskPercent}
                            status={diskPercent >= 100 ? 'exception' : 'active'}
                            strokeColor={{ '0%': '#108ee9', '90%': '#faad14', '100%': '#f5222d' }}
                        />
                        <div style={{ marginTop: 16, fontSize: 14, color: '#666' }}>
                            已用 {formatMB(stats.disk_usage)} / 总配额 {formatMB(stats.total_disk_quota)}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title={<span><ThunderboltOutlined /> 带宽用量（本月）</span>}>
                        <Progress
                            percent={bandwidthPercent}
                            status={bandwidthPercent >= 100 ? 'exception' : 'active'}
                            strokeColor={{ '0%': '#108ee9', '90%': '#faad14', '100%': '#f5222d' }}
                        />
                        <div style={{ marginTop: 16, fontSize: 14, color: '#666' }}>
                            已用 {formatGB(stats.bandwidth_usage)} / 总配额 {formatGB(stats.total_bandwidth_quota)}
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={24}>
                    <Card title="节点详情">
                        <Table
                            columns={nodeColumns}
                            dataSource={nodes}
                            loading={loading}
                            rowKey="id"
                            scroll={{ x: 'max-content' }}
                            locale={{
                                emptyText: loading ? ' ' : (
                                    <Empty description="暂无节点数据" />
                                ),
                            }}
                            pagination={{ pageSize: 10 }}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Statistics;
